import { useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  onLoadMore?: () => void;
  enabled?: boolean;
  threshold?: number;
}

/**
 * Hook that triggers `onLoadMore` when the referenced element is visible.
 * Returns a ref you should attach to the last item in the list.
 */
export function useInfiniteScroll<T extends HTMLElement>({
  onLoadMore,
  enabled = true,
  threshold = 0.5
}: UseInfiniteScrollOptions) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!enabled || !onLoadMore) return;

    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      { threshold }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [enabled, onLoadMore, threshold]);

  return ref;
}
